import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  alpha,
  useTheme,
  Fade,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PoolIcon from "@mui/icons-material/Pool";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import Method1 from "./Method1";
import Method2 from "./Method2";
import {
  CameraExportButton,
  exportComponentAsImage,
} from "../../../../utils/export/componentExporter";

const FTCCalculator = ({
  foreignIncomeData,
  mainComputationData,
  onRecordData,
}) => {
  const exportRef = useRef(null);
  const exportRef2 = useRef(null);

  const [method1Results, setMethod1Results] = useState([]);
  const [method2Results, setMethod2Results] = useState([]);
  const [totalMethod1FTC, setTotalMethod1FTC] = useState(0);
  const [totalMethod2FTC, setTotalMethod2FTC] = useState(0);
  const [totalFTC, setTotalFTC] = useState(0);

  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, "CountryByCountry");
    }
  };

  const handleExportAsImage2 = async (e) => {
    e.stopPropagation();
    if (exportRef2.current) {
      await exportComponentAsImage(exportRef2.current, "FTCPooling");
    }
  };

  useEffect(() => {
    calculateFTC();
  }, [foreignIncomeData, mainComputationData]);

  const calculateFTC = () => {
    const savedRows = foreignIncomeData?.filter((row) => row.isLocked);
    const method1Rows = [];
    const method2Rows = [];

    savedRows?.forEach((row) => {
      // Use the converted SGD amount for foreign tax
      const foreignTaxSGD = row.foreignTaxSufferedSGD || row.foreignTaxSuffered;

      if (foreignTaxSGD === 0) return; // Skip rows with no foreign tax

      const calculation = calculateSingaporeTax(
        row,
        mainComputationData.computedData
      );
      const singaporeTax = calculation.amount.toFixed(2);
      const originForeignTax = row.foreignTaxSuffered.toFixed(2);
      const foreignTax = foreignTaxSGD.toFixed(2);
      const ftcAmount = Math.min(singaporeTax, foreignTax);

      const result = {
        description: `${
          row.isPopulated ? row.source?.sch_name : row.scheduleName
        } (${row.incomeType})`,
        singaporeTax,
        foreignTax,
        originForeignTax,
        ftcAmount,
        country: row.country,
        incomeType: row.incomeType,
        isPooled: row.ftcPooling,
        calculationSteps: calculation.steps,
        netIncome: row.netIncome,
      };

      if (row.ftcPooling) {
        method2Rows.push(result);
      } else {
        method1Rows.push(result);
      }
    });

    setMethod1Results(method1Rows);
    setMethod2Results(method2Rows);

    const method1Total = method1Rows?.reduce(
      (sum, row) => sum + row.ftcAmount,
      0
    );

    const method2TotalSingaporeTax = method2Rows?.reduce(
      (sum, row) => sum + Number(row.singaporeTax),
      0
    );
    const method2TotalForeignTax = method2Rows?.reduce(
      (sum, row) => sum + Number(row.foreignTax),
      0
    );
    const method2TotalOriginForeignTax = method2Rows?.reduce(
      (sum, row) => sum + Number(row.originForeignTax),
      0
    );
    const method2Total = Math.min(
      method2TotalSingaporeTax,
      method2TotalForeignTax
    );

    const totalFTC = method1Total + method2Total;
    setTotalMethod1FTC(method1Total);
    setTotalMethod2FTC(method2Total);
    setTotalFTC(totalFTC);

    onRecordData({
      method1Results: method1Rows,
      method2Results: method2Rows,
      totalMethod1FTC: method1Total,
      totalMethod2FTC: method2Total,
      totalMethod2SingaporeTax: method2TotalSingaporeTax,
      totalMethod2ForeignTax: method2TotalForeignTax,
      totalMethod2OriginForeignTax: method2TotalOriginForeignTax,
      totalFTC: totalFTC,
    });
  };

  const calculateSingaporeTax = (row, mainData) => {
    const { incomeType, grossIncome, netIncome } = row;
    const {
      totalRevenue,
      adjustedProfitLoss,
      otherSourcedIncome,
      statutoryIncome,
      taxPayable,
    } = mainData;

    if (incomeType === "Trade / Branch Profits") {
      // Formula: (A / B) × (C / E) × F
      // A = Net Income, B = Total Revenue, C = Adjusted Profit Loss, E = Statutory Income, F = Tax Payable
      if (totalRevenue === 0 || statutoryIncome === 0)
        return { amount: 0, steps: [] };
      const step1 = grossIncome / totalRevenue;
      const step2 = adjustedProfitLoss / statutoryIncome;
      const step3 = step1 * step2;
      const result = step3 * taxPayable;

      const steps = [
        `FTC Amount: (A / B) × (C / E) × F`,
        ``,
        `(${formatAmount(grossIncome)} / ${formatAmount(
          totalRevenue
        )}) × (${formatAmount(adjustedProfitLoss)} / ${formatAmount(
          statutoryIncome
        )}) × ${formatAmount(taxPayable, false)} = ${formatAmount(
          result,
          false
        )}`,
      ];

      return { amount: result, steps };
    } else {
      // Formula: (D / E) × F
      // D = Net Income, E = Statutory Income, F = Tax Payable
      if (statutoryIncome === 0) return { amount: 0, steps: [] };
      const step1 = Math.round(netIncome) / Math.round(statutoryIncome);
      const result = step1 * taxPayable;

      const steps = [
        `FTC Amount: (D / E) × F`,
        ``,
        `(${formatAmount(netIncome)} / ${formatAmount(
          statutoryIncome
        )}) × ${formatAmount(taxPayable, false)} = ${formatAmount(
          result,
          false
        )}`,
      ];

      return { amount: result, steps };
    }
  };

  const hasData = foreignIncomeData?.some(
    (row) => row.isLocked && row.foreignTaxSuffered > 0
  );
  const hasMainComputationData =
    mainComputationData.computedData.statutoryIncome > 0 &&
    mainComputationData.computedData.taxPayable > 0;

  const theme = useTheme();

  if (!hasData || !hasMainComputationData) {
    return (
      <Box>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.info.main,
              0.08
            )}, ${alpha(theme.palette.info.main, 0.04)})`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: `linear-gradient(90deg, ${
                theme.palette.info.main
              }, ${alpha(theme.palette.info.main, 0.6)})`,
            },
          }}
        >
          <Stack direction='row' spacing={2} alignItems='flex-start'>
            <Box
              sx={{
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                flexShrink: 0,
              }}
            >
              <AccountBalanceOutlinedIcon
                sx={{
                  fontSize: 20,
                  color: "info.main",
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='subtitle2'
                sx={{
                  fontWeight: 600,
                  color: "info.main",
                  mb: 0.5,
                  fontSize: "0.9rem",
                }}
              >
                Data Required
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.5,
                  fontSize: "0.85rem",
                }}
              >
                Please complete Section 1 (save foreign income data) and Section
                2 (enter main computation data) to calculate FTC.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Method 1: Country-by-Country FTC */}
      {method1Results.length > 0 && (
        <Fade in={true} timeout={300}>
          <Paper
            ref={exportRef}
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            }}
          >
            {/* Accordion for Method 1 Working Table */}
            <Accordion
              defaultExpanded
              sx={{
                boxShadow: "none",
                background: "none",
                borderRadius: 0,
                "&:before": { display: "none" },
                mb: 0,
              }}
            >
              <AccordionSummary
                expandIcon={
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 1,
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.08),
                    }}
                  >
                    <ExpandMoreIcon sx={{ color: "primary.main" }} />
                  </Box>
                }
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.08
                  )}, ${alpha(theme.palette.primary.main, 0.04)})`,
                  borderBottom: `1px solid ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )}`,
                  minHeight: 0,
                  "& .MuiAccordionSummary-content": {
                    m: 0,
                    alignItems: "center",
                  },
                }}
              >
                <Stack
                  direction='row'
                  spacing={2}
                  alignItems='center'
                  sx={{ width: "100%" }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 2,
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.12),
                    }}
                  >
                    <AccountBalanceOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: "primary.main",
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant='h6'
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: "1.1rem",
                      }}
                    >
                      Foreign Tax Credit (Country-by-Country)
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.85rem",
                      }}
                    >
                      Individual calculation for each foreign income source
                    </Typography>
                  </Box>
                  <CameraExportButton
                    onClick={handleExportAsImage}
                    sx={{ ml: 1 }}
                  />
                  <Box sx={{ textAlign: "right", pr: 3 }}>
                    <Typography
                      variant='h4'
                      sx={{
                        fontWeight: 700,
                        color: "primary.main",
                        fontSize: "1.8rem",
                      }}
                    >
                      {formatAmount(totalMethod1FTC, false)}
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.75rem",
                      }}
                    >
                      {method1Results.length} item
                      {method1Results.length > 1 ? "s" : ""} calculated
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {/* Working Table */}
                <Box sx={{ p: 3 }}>
                  <Method1
                    method1Results={method1Results}
                    totalMethod1FTC={totalMethod1FTC}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Fade>
      )}

      {/* FTC Pooling Section (Method 2) */}
      {method2Results?.length > 0 && (
        <Fade in={true} timeout={500}>
          <Paper
            ref={exportRef2}
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            }}
          >
            {/* Accordion for Method 2 Working Table */}
            <Accordion
              defaultExpanded
              sx={{
                boxShadow: "none",
                background: "none",
                borderRadius: 0,
                "&:before": { display: "none" },
                mb: 0,
              }}
            >
              <AccordionSummary
                expandIcon={
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 1,
                      bgcolor: (theme) =>
                        alpha(theme.palette.secondary.main, 0.08),
                    }}
                  >
                    <ExpandMoreIcon sx={{ color: "secondary.main" }} />
                  </Box>
                }
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.secondary.main,
                    0.08
                  )}, ${alpha(theme.palette.secondary.main, 0.04)})`,
                  borderBottom: `1px solid ${alpha(
                    theme.palette.secondary.main,
                    0.1
                  )}`,
                  minHeight: 0,
                  "& .MuiAccordionSummary-content": {
                    m: 0,
                    alignItems: "center",
                  },
                }}
              >
                <Stack
                  direction='row'
                  spacing={2}
                  alignItems='center'
                  sx={{ width: "100%" }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 2,
                      bgcolor: (theme) =>
                        alpha(theme.palette.secondary.main, 0.12),
                    }}
                  >
                    <PoolIcon
                      sx={{
                        fontSize: 20,
                        color: "secondary.main",
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant='h6'
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: "1.1rem",
                      }}
                    >
                      FTC Pooling
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.85rem",
                      }}
                    >
                      Detailed calculation breakdown for pooled foreign tax
                      credits
                    </Typography>
                  </Box>
                  <CameraExportButton
                    onClick={handleExportAsImage2}
                    sx={{ mr: 1 }}
                  />
                  <Box sx={{ textAlign: "right", pr: 3 }}>
                    <Typography
                      variant='h4'
                      sx={{
                        fontWeight: 700,
                        color: "secondary.main",
                        fontSize: "1.8rem",
                      }}
                    >
                      {formatAmount(totalMethod2FTC, false)}
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.75rem",
                      }}
                    >
                      {method2Results.length} item
                      {method2Results.length > 1 ? "s" : ""} pooled
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {/* Working Table */}
                <Box sx={{ p: 3 }}>
                  <Method2
                    method2Results={method2Results}
                    totalMethod2FTC={totalMethod2FTC}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Fade>
      )}

      {/* Total Summary */}
      {(method1Results?.length > 0 || method2Results?.length > 0) && (
        <Fade in={true} timeout={700}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mt: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.success.main,
                0.08
              )}, ${alpha(theme.palette.success.main, 0.04)})`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Stack direction='row' spacing={2} alignItems='center'>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 2,
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                  flexShrink: 0,
                }}
              >
                <CheckCircleOutlinedIcon
                  sx={{
                    fontSize: 20,
                    color: "success.main",
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontWeight: 600,
                    color: "success.main",
                    mb: 0.5,
                    fontSize: "0.9rem",
                  }}
                >
                  Total Claimable Foreign Tax Credit
                </Typography>
                <Typography
                  variant='h4'
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    fontSize: "1.8rem",
                  }}
                >
                  {formatAmount(totalFTC, false)}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.85rem",
                    mt: 1,
                  }}
                >
                  This FTC amount can be claimed as a tax deduction in main
                  computation.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

export default FTCCalculator;
